// pages/FormViewerPage/services/formViewerService/validation.ts — Field validation helpers
import type { Form } from '../../../../types';
import type { AddressData } from '../../../../components/AddressInput';
import { getFlatRegionKey } from './shipping';
import { isPostalMatchingProvince } from '../../../../utils/postalZone';
import { ms } from '../../../../locales/ms';
import { validateMyAddress } from './validateMyAddress';

export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const MAX_NAME_LENGTH = 25;
export const MIN_ADDRESS_LENGTH = 30;
export const MAX_ADDRESS_LENGTH = 150;

const NAME_ADDRESS_PATTERNS = [
    /\b(jl\.?|jalan|gang|gg\.?|dusun|dsn|lorong)\b/i,
    /\b(rt\s*\d+|rw\s*\d+)\b/i,
    /\b(kelurahan|desa|kecamatan|kec\.?)\b/i,
    /\b(no\.?\s*\d+|nomor)\b/i,
    /\b(kab\.?|kota|provinsi)\b/i,
];

// Nama ketat (paritas backend csFormHelpers.js): tolak angka, alamat, dan
// isi tanpa huruf. "01111111212adas" wajib ditolak, "Siti Aminah S.Pd" lolos.
export const validateCustomerName = (name: string, minLength: number = 3): string | null => {
    const trimmed = String(name || '').trim();
    if (!trimmed || trimmed.length < minLength) return `Nama minimal ${minLength} karakter`;
    if (trimmed.length > MAX_NAME_LENGTH) return `Nama maksimal ${MAX_NAME_LENGTH} karakter`;
    if (/\d/.test(trimmed)) return 'Nama tidak boleh mengandung angka';
    if (NAME_ADDRESS_PATTERNS.some(p => p.test(trimmed))) return 'Nama tidak boleh mengandung alamat';
    const letters = trimmed.replace(/[^A-Za-zÀ-ÿ]/g, '');
    if (letters.length < 2) return 'Nama tidak valid';
    const nospace = trimmed.replace(/\s+/g, '');
    if (nospace.length >= 3 && /^(\S)\1+$/.test(nospace)) return 'Nama tidak valid';
    return null;
};

const SEQUENTIAL_FULL = /^(?:0123456789|1234567890|0987654321|9876543210|0123456789013|1234567890123)$/;

export const validateWhatsappNumber = (phone: string) => {
    const cleaned = String(phone || '').replace(/\D/g, '');
    if (cleaned.length < 10 || cleaned.length > 15) return { isValid: false };
    // Tolak prefix negara ganda/rusak (data nyata: 628628.../622525... 16 digit,
    // 6260 sisa bug MY) — ini lolos cek panjang tapi tak pernah bisa dihubungi.
    if (/^(62){2,}|^(60){2,}|^6260|^6062/.test(cleaned)) return { isValid: false };
    const core = cleaned.startsWith('62') || cleaned.startsWith('60')
        ? cleaned.slice(2)
        : cleaned.startsWith('0') ? cleaned.slice(1) : cleaned;
    if (core.length < 8 || core.length > 13 || core.startsWith('0')) return { isValid: false };
    // Tolak pola jelas-jelas sampah tapi lolos cek panjang: digit berulang
    // (08111111111), semua digit sama, atau variasi cuma 2 digit (0808080808).
    // Nomor real hampir tak pernah begini. Sengaja TIDAK tolak substring urut
    // (cth 081234567890 tetap valid) untuk hindari false-positive nomor real.
    if (/^(\d)\1+$/.test(core)) return { isValid: false };
    if (/(.)\1{5,}/.test(core)) return { isValid: false };
    if (core.length >= 8 && new Set(core.split('')).size <= 2) return { isValid: false };
    if (SEQUENTIAL_FULL.test(core)) return { isValid: false };
    return { isValid: true };
};

export function validateCustomerFields(
    form: Form | null,
    customerData: { name: string; whatsapp: string; email: string },
    addressData: AddressData,
    isSimpleEmbed: boolean = false,
    herbalClaimMode: 'wa' | 'claim_ongkir' = 'wa'
): Record<string, string> {
    const errors: Record<string, string> = {};
    const customerFields: Record<string, any> = form?.customerFields || {};
    const nameVal = customerData.name || '';
    const minName = customerFields.name?.minLength || 3;
    if (customerFields.name?.visible && customerFields.name?.required) {
        const nameErr = validateCustomerName(nameVal, minName);
        if (nameErr) {
            errors.name = nameErr.startsWith('Nama minimal')
                ? ms(form, m => m.nameMin(minName), nameErr)
                : nameErr.startsWith('Nama maksimal')
                    ? ms(form, m => m.nameMax(MAX_NAME_LENGTH), nameErr)
                    : nameErr;
        }
    } else if (nameVal && nameVal.trim()) {
        // Field opsional: kalau diisi tetap wajib valid isinya (tanpa paksa minLength).
        const nameErr = validateCustomerName(nameVal, 0);
        if (nameErr) errors.name = nameErr;
    }
    if (!isSimpleEmbed && customerFields.email?.visible && customerFields.email?.required && customerData.email && !isValidEmail(customerData.email)) {
        errors.email = ms(form, m => m.emailInvalid, 'Format email tidak valid');
    }
    if (customerFields.whatsapp?.visible && customerFields.whatsapp?.required) {
        const wc = validateWhatsappNumber(customerData.whatsapp);
        if (!wc.isValid) errors.whatsapp = ms(form, m => m.whatsappInvalid, 'Nomor WhatsApp tidak valid');
    }
    const mode = form?.addressInputMode;
    // Mode Herbal (simple), Embed Simpel, atau Konsultasi (lead-only: nama + WA saja):
    if (mode === 'simple' || isSimpleEmbed || form?.thankYouPage?.consultationMode) {
        if (herbalClaimMode === 'claim_ongkir') {
            const addr = (addressData.detailAddress || addressData.fullAddress || '').trim();
            if (!addr || addr.length < 8) {
                errors.address = 'Mohon tulis alamat rumah Anda untuk klaim potongan ongkir (min. 8 karakter)';
            }
        }
        return errors;
    }
    // Form MY: Negeri + Bandar + Poskod — cabang di validateMyAddress.ts.
    const myErrors = validateMyAddress(form, customerData, addressData);
    if (myErrors) return myErrors;
    if (mode === 'flat_regular' || mode === 'dropdown') {
        // ATURAN 2026-10-04: mode dropdown = dropdown WAJIB (bukan opsional).
        // Flag required=false di Form Editor DIABAIKAN — visible = wajib isi.
        // (Insiden order kosong #6540598b: required=false → lolos tanpa wilayah.)
        if (customerFields.province?.visible && !addressData.province) errors.province = 'Pilih provinsi';
        const isFlatZone = mode === 'flat_regular' && !!addressData.province && !!getFlatRegionKey(addressData.province);
        if (!isFlatZone) {
            if (customerFields.city?.visible && !addressData.city) errors.city = 'Pilih kota/kabupaten';
            if (customerFields.district?.visible && !addressData.district) errors.district = 'Pilih kecamatan';
            if (customerFields.village?.visible && !addressData.village) errors.village = 'Pilih kelurahan';
        }
    } else if (mode === 'none') {
        if (customerFields.address?.visible && customerFields.address?.required) {
            const addr = (addressData.detailAddress || addressData.fullAddress || '').trim();
            const minChars = Math.max(MIN_ADDRESS_LENGTH, customerFields.address.minCharacters || 0);
            if (!addr || addr.length < minChars) errors.address = `Alamat minimal ${minChars} karakter`;
        }
    } else if (mode === 'everpro') {
        // Sama: dropdown wajib (aturan 2026-10-04).
        if (customerFields.province?.visible && !addressData.province) errors.province = 'Pilih provinsi';
        if (customerFields.city?.visible && !addressData.city) errors.city = 'Pilih kota/kabupaten';
    }
    // Silang kode pos vs provinsi (insiden #451c1d74: 65311-Malang tersimpan
    // utk kota Makassar → tarif intra-Jawa dipakai ke Sulawesi). Kosong → lolos
    // (backend resolve otomatis); salah → tolak dengan pesan jelas.
    {
        const pc = String((addressData as any)?.postalCode || '').replace(/\D/g, '');
        const prov = (addressData as any)?.province || '';
        if (pc.length >= 5 && prov && !isPostalMatchingProvince(pc, prov)) {
            errors.postalCode = 'Kode pos tidak sesuai dengan provinsi — periksa kembali';
        }
    }
    // Blok generik (mode lampau/tak dikenal): Mode Simpel sudah early-return di atas,
    // jadi blok ini takkan berjalan untuk 'simple'.
    if (customerFields.address?.visible && customerFields.address?.required) {
        const addr = (addressData.detailAddress || addressData.fullAddress || '').trim();
        const minChars = Math.max(MIN_ADDRESS_LENGTH, customerFields.address.minCharacters || 0);
        if (!addr || addr.length < minChars) errors.address = `Alamat minimal ${minChars} karakter`;
        else if (addr.length > MAX_ADDRESS_LENGTH) errors.address = `Alamat maksimal ${MAX_ADDRESS_LENGTH} karakter`;
    }
    return errors;
}
